import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Home, Users, Banknote, MessageCircle, Phone } from "lucide-react";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";

const STEPS = [
  { icon: ShieldCheck, n: "01", t: "Title Audit & Onboarding", d: "Independent legal opinion, RERA verification, and encumbrance certificate before any handover." },
  { icon: Home,        n: "02", t: "Property Lock & Physical Care", d: "Monthly inspection reports, utility management, deep-clean rotation and CCTV where applicable." },
  { icon: Users,       n: "03", t: "Tenant Vetting", d: "Employment, credit and social checks. Corporate lease preference. Full documentation via e-stamp." },
  { icon: Banknote,    n: "04", t: "Auto Rent Repatriation", d: "NRO/NRE-linked collection, TDS filing, and repatriation via FEMA-compliant Form 15CA/CB." },
];

const COUNTRY_CODES = ["+1", "+44", "+61", "+65", "+971", "+91"];

export default function NRI() {
  const [f, setF] = useState({
    name: "", email: "", country_code: "+971", phone: "",
    country: "UAE", service: "Full Service", whatsapp_preferred: true, notes: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[0-9]{7,15}$/.test(f.phone)) return toast.error("Enter a valid phone number");
    setLoading(true);
    try {
      await api.post("/nri/enquiry", f);
      toast.success("Received. An NRI advisor will contact you within 24 hours.");
      setF({ ...f, name: "", email: "", phone: "", notes: "" });
    } catch { toast.error("Failed to submit"); }
    finally { setLoading(false); }
  };

  return (
    <div className="pt-[112px]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="eyebrow mb-6 flex items-center gap-3"><span className="accent-dot" /> NRI Asset Management Hub</div>
        <h1 className="display-1 max-w-5xl">
          <MaskedLines lines={["Governance for the", "properties you can't", "physically be near."]} />
        </h1>
        <p className="mt-10 max-w-2xl text-[15px] leading-[1.8] text-[#EDE6D3]/75">
          A white-glove operating layer for NRIs, HNIs and family offices — building tenants, filings, repairs and repatriation into a single monthly statement.
        </p>
      </section>

      {/* Split hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-20 grid grid-cols-1 md:grid-cols-12 gap-8">
        <Reveal className="md:col-span-7">
          <div className="relative a-16-10 overflow-hidden">
            <img src="https://images.pexels.com/photos/6585598/pexels-photo-6585598.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="NRI property" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="md:col-span-5 flex flex-col justify-end">
          <Reveal delay={0.1}>
            <div className="chapter-num mb-4">— Signal</div>
            <div className="font-serif text-[26px] md:text-[32px] leading-tight">
              "The single line item on my P&L I stopped worrying about."
            </div>
            <div className="mt-4 text-[12px] tracking-[2px] uppercase text-[#EDE6D3]/60">— A. Malhotra · Dubai · Managing 4 units</div>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-40" data-testid="nri-timeline">
        <div className="eyebrow mb-6">Process · Four Stages</div>
        <h2 className="font-serif text-[44px] md:text-[64px] leading-[0.95] tracking-tight max-w-3xl">A protocol, not a promise.</h2>

        <div className="mt-16 relative">
          <div className="hidden md:block absolute top-[64px] left-0 right-0 hairline-bronze" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="flex flex-col">
                  <div className="w-16 h-16 bg-[#0A2342] border border-[#D4AF37] flex items-center justify-center relative z-10">
                    <s.icon size={22} strokeWidth={1.4} color="#D4AF37" />
                  </div>
                  <div className="chapter-num mt-8">— {s.n}</div>
                  <div className="mt-3 font-serif text-[22px] md:text-[26px] leading-tight">{s.t}</div>
                  <p className="mt-4 text-[13px] leading-[1.8] text-[#EDE6D3]/70">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-40" data-testid="nri-form-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="chapter-num mb-3">— Contact</div>
            <h2 className="font-serif text-[38px] md:text-[52px] leading-tight tracking-tight">A single email starts it.</h2>
            <p className="mt-6 text-[14px] leading-[1.8] text-[#EDE6D3]/70">Tell us where the property sits and where you are — we handle the twelve time-zone problems that follow.</p>
            <div className="mt-10 space-y-4 text-[13px] text-[#EDE6D3]/75">
              <div className="flex items-center gap-3"><Phone size={14} color="#D4AF37" /> <span>+91 9900186556</span></div>
              <div className="flex items-center gap-3"><MessageCircle size={14} color="#D4AF37" /> <span>WhatsApp preferred · we reply within 4 hours</span></div>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-7 card-sand p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6" data-testid="nri-form">
            <div className="md:col-span-2 flex items-center justify-between mb-2">
              <div className="eyebrow">NRI Enquiry</div>
              <div className="chapter-num">// Secure Submission</div>
            </div>
            <div><div className="bb-label">Full Name</div><input required className="bb-input" value={f.name} onChange={set("name")} data-testid="nri-name" /></div>
            <div><div className="bb-label">Email</div><input required type="email" className="bb-input" value={f.email} onChange={set("email")} data-testid="nri-email" /></div>
            <div><div className="bb-label">Country</div>
              <select className="bb-input" value={f.country} onChange={set("country")} data-testid="nri-country">
                <option>UAE</option><option>USA</option><option>UK</option><option>Singapore</option><option>Australia</option><option>Canada</option><option>Other</option>
              </select>
            </div>
            <div>
              <div className="bb-label">Phone</div>
              <div className="flex gap-3">
                <select className="bb-input w-24" value={f.country_code} onChange={set("country_code")} data-testid="nri-cc">
                  {COUNTRY_CODES.map((c) => <option key={c}>{c}</option>)}
                </select>
                <input required className="bb-input flex-1" value={f.phone} onChange={set("phone")} placeholder="Number only" data-testid="nri-phone" />
              </div>
            </div>
            <div className="md:col-span-2"><div className="bb-label">Service Interest</div>
              <select className="bb-input" value={f.service} onChange={set("service")} data-testid="nri-service">
                <option>Rental Management</option><option>Property Care</option><option>Tenant Vetting</option><option>Full Service</option>
              </select>
            </div>
            <div className="md:col-span-2"><div className="bb-label">Message</div>
              <textarea className="bb-input" rows={3} value={f.notes} onChange={set("notes")} data-testid="nri-notes" />
            </div>
            <label className="md:col-span-2 flex items-center gap-3 cursor-pointer" data-testid="nri-wa">
              <span className="relative inline-block w-9 h-5">
                <input type="checkbox" className="peer sr-only" checked={f.whatsapp_preferred} onChange={(e) => setF({ ...f, whatsapp_preferred: e.target.checked })} />
                <span className="absolute inset-0 bg-[#EDE6D3]/15 peer-checked:bg-[#D4AF37] transition-colors" />
                <span className="absolute top-[2px] left-[2px] w-4 h-4 bg-[#0A2342] peer-checked:translate-x-4 transition-transform" />
              </span>
              <span className="text-[11px] tracking-[2px] uppercase">Prefer WhatsApp for replies</span>
            </label>
            <button type="submit" disabled={loading} className="btn-primary md:col-span-2 justify-center disabled:opacity-50" data-testid="nri-submit">
              {loading ? "Submitting…" : "Request NRI Consultation"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
